import {
  useEffect, useReducer, useCallback, useState,
} from 'react';

import { search } from '@snapbrains/snapav/src/search';
import { bestMatch } from '@snapbrains/snapav/src/best_match';

import useDebouncedValue from './use_debounced_value';

export const requestState = {
  requested: 'requested',
  inflight: 'inflight',
  completed: 'completed',
  unknown: 'unknown',
};

export function genQueryKey(searchArgs) {
  if (!searchArgs) {
    return null;
  }

  const {
    query = '',
    sortOrder,
    merchantIds = [],
    category,
    subCategory,
    customCategory,
    brand,
    onSaleOnly,
    onSaleLevel,
    minPrice,
    maxPrice,
    department,
    merchantCategoryIds = [],
  } = searchArgs;

  return `${query}-${sortOrder}-${merchantIds.join('-')}-${category}-${customCategory}-${subCategory}-${brand}-${onSaleOnly}-${onSaleLevel}-${minPrice}-${maxPrice}-${department}-${merchantCategoryIds.join('-')}`;
}

function safeGetRequest(state, key, page) {
  if (state[key] && state[key][page]) {
    return state[key][page];
  }

  return {
    status: requestState.unknown,
    hasMore: true,
    results: null,
    loading: null,
  };
}

function reducer(state, action) {
  switch (action.type) {
  case 'setStatus': {
    const {
      page, key, status, hasMore, results,
    } = action;
    const tmp = { ...state };
    if (!tmp[key]) {
      tmp[key] = {};
    }
    tmp[key][page] = {
      status,
      hasMore,
      results,
    };

    return tmp;
  }
  default:
    throw new Error('Invalid type');
  }
}

function shouldDispatchUnfiltered(searchArgs) {
  const {
    merchantIds,
    category,
    customCategory,
    subCategory,
    brand,
    onSaleOnly,
    searchOnSaleOnly, // Legacy value that is the same as onSaleOnly
    onSaleLevel,
    minPrice,
    maxPrice,
    department,
    merchantCategoryIds,
  } = searchArgs;

  return merchantIds.length <= 0
    && (category === null || category === '')
    && (customCategory === null || customCategory === '')
    && (subCategory === null || subCategory === '')
    && (brand === null || brand === '')
    && (department === null || department === '')
    && !onSaleOnly
    && !searchOnSaleOnly
    && (onSaleLevel === null || onSaleLevel === 0)
    && (minPrice === null || minPrice === 0)
    && maxPrice === null
    && merchantCategoryIds.length <= 0;
}

function unfilteredSearchArgs(query, searchArgs) {
  const args = {
    avantlinkAffiliateId: searchArgs.avantlinkAffiliateId,
    avantlinkWebsiteId: searchArgs.avantlinkWebsiteId,
    query,
    page: 0,
    resultCount: 1000,
    merchantIds: [],
    category: '',
    customCategory: '',
    subCategory: '',
    brand: '',
    onSaleOnly: false,
    onSaleLevel: null,
    minPrice: null,
    maxPrice: null,
    department: null,
    merchantCategoryIds: [],
  };

  // Allow for a custom search API endpoint. Handy if the search needs to be proxied.
  if (searchArgs.searchUrl) {
    args.searchUrl = searchArgs.searchUrl;
  }

  // Allow passing headers that can be sent to the search API endpoint
  if (searchArgs.searchHeaders) {
    args.searchHeaders = searchArgs.searchHeaders;
  }

  return args;
}

export default function useSearch(
  searchArgs,
  useBestMatch = false,
  includeUnfilteredResults = true,
) {
  const {
    query,
    page,
    resultCount,
  } = searchArgs;

  const [state, dispatch] = useReducer(reducer, {});
  const debouncedQuery = useDebouncedValue(query, 300);
  const unfilteredKey = debouncedQuery;
  const key = genQueryKey({ ...searchArgs, query: debouncedQuery });
  const currReq = safeGetRequest(state, key, page);
  const [hasSearched, setHasSearched] = useState(false);

  const allResults = state;

  const goSearch = useCallback(() => {
    dispatch({
      type: 'setStatus',
      key,
      page,
      status: requestState.inflight,
    });

    const theFunc = useBestMatch ? bestMatch : search;

    theFunc(
      { ...searchArgs, query: debouncedQuery },
      (results) => {
        dispatch({
          type: 'setStatus',
          key,
          page,
          results,
          status: requestState.completed,
          hasMore: results?.length === resultCount,
        });

        if (shouldDispatchUnfiltered(searchArgs)) {
          dispatch({
            type: 'setStatus',
            key: unfilteredKey,
            page,
            results,
            status: requestState.completed,
            hasMore: results?.length === resultCount,
          });
        } else if (includeUnfilteredResults && typeof (allResults[unfilteredKey]) === 'undefined') {
          // Make a second query to get unfiltered results to populate the filters
          search(unfilteredSearchArgs(debouncedQuery, searchArgs), (searchResults) => {
            dispatch({
              type: 'setStatus',
              key: unfilteredKey,
              page,
              results: searchResults,
              status: requestState.completed,
              hasMore: searchResults.length === resultCount,
            });
          });
        }
      },
    );
  }, [
    key,
    page,
    useBestMatch,
    searchArgs,
    debouncedQuery,
    resultCount,
    includeUnfilteredResults,
    allResults,
    unfilteredKey,
  ]);

  useEffect(() => {
    if (searchArgs?.query?.length > 2 && currReq.status === requestState.unknown) {
      dispatch({
        type: 'setStatus',
        key,
        page,
        status: requestState.requested,
      });
    }
  }, [searchArgs?.query?.length, key, page, currReq.status]);

  useEffect(() => {
    if (debouncedQuery?.length > 2 && currReq.status === requestState.requested) {
      goSearch();
    }
  }, [currReq.status, debouncedQuery?.length, goSearch]);

  useEffect(() => {
    if (!hasSearched && currReq.status === requestState.completed) {
      setHasSearched(true);
    }
  }, [hasSearched, currReq.status]);

  useEffect(() => {
    setHasSearched(false);
  }, [debouncedQuery]);

  let results = [];
  if (allResults[key]) {
    const pages = Object.keys(allResults[key]);
    for (let i = 0; i < pages.length; i++) {
      if (allResults[key][pages[i]].status === requestState.completed) {
        results = results.concat(allResults[key][pages[i]].results);
      }
    }
  }

  let unfilteredResults = [];
  if (allResults[unfilteredKey]) {
    const pages = Object.keys(allResults[unfilteredKey]);
    for (let i = 0; i < pages.length; i++) {
      if (allResults[unfilteredKey][pages[i]].status === requestState.completed) {
        unfilteredResults = unfilteredResults.concat(allResults[unfilteredKey][pages[i]].results);
      }
    }
  }

  let loading = false;
  if (currReq.status === requestState.requested
    || currReq.status === requestState.inflight) {
    loading = true;
  } else if (currReq.status === requestState.completed) {
    loading = false;
  }

  const hasMore = currReq.hasMore === true
    || typeof currReq.hasMore === 'undefined';

  return {
    unfilteredResults,
    results,
    key,
    loading,
    hasSearched,
    hasMore,
  };
}
